import React, { useState } from 'react';
import ReglementForm from '../components/Forms/ReglementForm';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../reduxStore/Store';
import { deleteReglement, setFilters, setSelectedReglement } from '../reduxStore/ReglementSlice';
import MyTable from '../components/MyTable';
import { getLabels } from '../constants/fieldLabels';
import { TableAction, TableHeader } from '../constants/interfaces';
import { Reglement } from '../models/models';
import ReglementFilters from '../components/Filters/ReglementFilters';
import { formatStringDate } from '../utils/utils';
import { useSnackbar } from '../components/SnackbarProvider';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ButtonType, TypeRapport, FormatRapport } from '../constants/enums';
import { useGenerateReport } from '../hooks/useGenerateReport';
import PrintMenuButton from '../components/PrintMenuButton';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import AppFilters from '../components/layout/AppFilters';

const formatMontant = (n: number) =>
  Number(n ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Carte de synthèse ────────────────────────────────────────────────────────
interface StatProps {
  label: string;
  value: string;
  hint?: string;
  icon: React.ReactNode;
  tone: 'neutral' | 'credit' | 'debit' | 'accent';
}
const TONES = {
  neutral: { fg: '#344054', bg: '#f2f4f7' },
  credit:  { fg: '#067647', bg: '#ecfdf3' },
  debit:   { fg: '#b42318', bg: '#fef3f2' },
  accent:  { fg: '#2563eb', bg: '#eef4ff' },
};
const StatCard: React.FC<StatProps> = ({ label, value, hint, icon, tone }) => {
  const c = TONES[tone];
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e4e7ec', borderRadius: '16px', p: 2.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 34, height: 34, borderRadius: '10px', bgcolor: c.bg, color: c.fg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '& .MuiSvgIcon-root': { fontSize: 19 },
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#667085' }}>{label}</Typography>
      </Box>
      <Typography sx={{ mt: 1.25, fontWeight: 800, fontSize: '1.35rem', color: c.fg, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      {hint && <Typography sx={{ mt: 0.25, fontSize: '0.75rem', color: '#98a2b3' }}>{hint}</Typography>}
    </Box>
  );
};

const GestionCaisse: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { reglementData, filters, total, totalCredit, totalDebit, soldeCaisse, selectedReglement } = useSelector((state: any) => state.ReglementSlice);
  const net = (totalCredit ?? 0) - (totalDebit ?? 0);
  const { selectedDepartement, isAllMode } = useSelector((state: any) => state.AppSlice);
  const { showSnackbar } = useSnackbar();
  const { generateReport, generating } = useGenerateReport();

  const [formOpen, setFormOpen] = useState(false);
  const openNew = () => {
    dispatch(setSelectedReglement(null));
    setFormOpen(true);
  };
  const openEdit = (r: Reglement) => {
    dispatch(setSelectedReglement(r));
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    dispatch(setSelectedReglement(null));
  };

  const handleGenerateReport = (format: FormatRapport) => {
    const { limit, offset, caisse, tiers, ...filterParams } = filters as any;
    // '' (tous les sens) → null pour le worker ; 'C'/'D' conservés. Évite un filtre SP sur chaîne vide.
    const sens = filterParams.sens || null;
    generateReport({
      type_rapport:   TypeRapport.LISTE_REGLEMENT,
      format,
      parametres:     { ...filterParams, sens, departement_id: selectedDepartement?.id ?? null },
      departement_id: selectedDepartement?.id ?? null,
      fileName:       `reglements_caisse_${format.toLowerCase()}`,
    });
  };

  const headers: TableHeader<Reglement>[] = [
    { key: 'date_reglement',   label: 'Date',      noTruncate: true, width: 100, render: (row) => formatStringDate(row.date_reglement) },
    // Visible seulement en mode "Tous départements" : dans un contexte de département
    // actif, toutes les lignes appartiennent déjà au même département (redondant).
    ...(isAllMode ? [{
      key: 'departement_nom' as const,
      label: 'Département',
      width: 130,
      render: (row: Reglement) => row.departement_nom ?? '—',
    }] : []),
    { key: 'numero_reglement', label: 'Numéro',    noTruncate: true, width: 110 },
    { key: 'montant',          label: 'Montant',   align: 'right', noTruncate: true, width: 110 },
    { key: 'reference',        label: 'Référence', width: 200 },
    { key: 'sens',             label: 'Sens',      width: 80, render: (row) => row.sens === 'C' ? 'Crédit' : 'Débit' },
    { key: 'caisse_id',        label: 'Caisse',    width: 130, render: (row) => row.caisse?.nom ?? 'N/A' },
    {
      key: 'tiers', label: 'Tiers', width: 160,
      render: (row) => {
        const nom = row.tiers?.nom;
        const prenom = row.tiers?.prenom;
        if (!nom && !prenom) return 'N/A';
        return `${nom || ''} ${prenom || ''}`.trim();
      }
    },
  ];

  const tableActions: TableAction<Reglement>[] = [
    {
      label: 'Edit',
      type: ButtonType.EDIT,
      callback: (reglement) => reglement && openEdit(reglement),
    },
    {
      label: 'Delete',
      type: ButtonType.DELETE,
      callback: async (reglement) => {
        if (reglement && window.confirm('Voulez-vous vraiment supprimer ce document ?')) {
          try {
            await dispatch(deleteReglement(reglement.id)).unwrap();
            showSnackbar('operation supprimé avec succès !', 'success');
          } catch (error) {
            showSnackbar((error as string) || 'Erreur lors de la suppression', 'error');
          }
        }
      },
    },
  ];

  const handlePageChange = (newPage: number, newRowsPerPage: number) => {
    dispatch(setFilters({ ...filters, offset: newPage * newRowsPerPage, limit: newRowsPerPage }));
  };

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title="Gestion Caisse"
        subtitle={`${total ?? 0} règlement${(total ?? 0) > 1 ? 's' : ''} sur la période`}
        icon={<AccountBalanceWalletIcon />}
        addLabel="Nouvelle opération"
        onAdd={openNew}
        actions={
          <PrintMenuButton
            onPdf={()   => handleGenerateReport(FormatRapport.PDF)}
            onExcel={() => handleGenerateReport(FormatRapport.EXCEL)}
            loading={generating}
          />
        }
      />

      {/* Synthèse */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          mb: 2,
        }}
      >
        <StatCard
          label="Solde de la caisse"
          value={soldeCaisse != null ? formatMontant(soldeCaisse) : '—'}
          hint={soldeCaisse != null ? 'Toutes dates' : 'Filtrez une caisse'}
          icon={<AccountBalanceWalletIcon />}
          tone={soldeCaisse == null ? 'neutral' : soldeCaisse >= 0 ? 'accent' : 'debit'}
        />
        <StatCard label="Encaissements" value={formatMontant(totalCredit)} hint="Sur la période filtrée" icon={<ArrowDownwardIcon />} tone="credit" />
        <StatCard label="Décaissements" value={formatMontant(totalDebit)} hint="Sur la période filtrée" icon={<ArrowUpwardIcon />} tone="debit" />
        <StatCard
          label="Net de la période"
          value={formatMontant(net)}
          hint="Encaissements − décaissements"
          icon={<SwapVertIcon />}
          tone={net > 0 ? 'credit' : net < 0 ? 'debit' : 'neutral'}
        />
      </Box>

      {/* Liste */}
      <AppSurface padding={0}>
        <AppFilters>
          <ReglementFilters />
        </AppFilters>
        <MyTable
          headers={headers}
          data={reglementData}
          onPageChange={handlePageChange}
          actions={tableActions}
          totalRows={total}
          showDetails
          fieldLabels={getLabels('Reglement')}
          tableKey="caisse_reglements"
        />
      </AppSurface>

      {/* Formulaire — panneau latéral */}
      <Drawer
        anchor="right"
        open={formOpen}
        onClose={closeForm}
        PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, bgcolor: '#fff' } }}
      >
        <Box
          sx={{
            px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
            borderBottom: '1px solid #e4e7ec', position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: 34, height: 34, borderRadius: '10px', bgcolor: '#eef4ff', color: '#2563eb',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <AccountBalanceWalletIcon fontSize="small" />
          </Box>
          <Typography sx={{ flexGrow: 1, fontWeight: 700, fontSize: '1rem', color: '#101828' }}>
            {selectedReglement ? "Modifier l'opération" : 'Nouvelle opération'}
          </Typography>
          <IconButton size="small" onClick={closeForm} aria-label="Fermer">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Le Drawer démonte son contenu à la fermeture → réinitialisation complète
            du formulaire à chaque réouverture. */}
        <Box sx={{ p: 2.5 }}>
          <ReglementForm onClose={closeForm} />
        </Box>
      </Drawer>
    </AppPage>
  );
};

export default GestionCaisse;
